import isLocalStorageAvailable from '@/utils/isLocalStorageAvailable'

// 1hr
const DEFAULT_CACHE_DURATION = 1000 * 60 * 60

const cacheResponse = (key: string, value: any, expiresAt: number) => {
  localStorage.setItem(
    `cache::${key}`,
    JSON.stringify({ data: value, expiresAt })
  )
}

const getCachedResponse = <T>(key: string): T | null => {
  const cacheKey = `cache::${key}`

  const stored = localStorage.getItem(cacheKey)
  if (!stored) return null

  try {
    const { data, expiresAt } = JSON.parse(stored)
    if (expiresAt > new Date().getTime()) {
      return data
    } else {
      localStorage.removeItem(cacheKey)
      return null
    }
  } catch (e) {
    return null
  }
}

type Listener<R> = (res: R) => void

export interface CachedActionOptions<R> {
  onFetchDone?: Listener<R>
  expiresAfter?: number
}

interface WrappedAction<R, A extends []> {
  (...args: A): Promise<R>
  withCache: (
    cacheKey: string,
    options?: CachedActionOptions<R>
  ) => (...args: A) => Promise<R>
}

export const cacheableAction = <R, A extends []>(
  action: (...args: A) => Promise<R>
): WrappedAction<R, A> => {
  const wrappedAction = async (...args: A) => await action(...args)

  wrappedAction.withCache = (
    cacheKey: string,
    options: CachedActionOptions<R> = {}
  ) => {
    const { onFetchDone, expiresAfter = DEFAULT_CACHE_DURATION } = options

    const execRequest = async (...args: A): Promise<R> => {
      const result = await action(...args)
      cacheResponse(cacheKey, result, new Date().getTime() + expiresAfter)

      return result
    }

    const execRequestWithEvent = async (...args: A): Promise<R> => {
      const result = await execRequest(...args)
      onFetchDone && onFetchDone(result)

      return result
    }

    return async (...args: A) => {
      if (!isLocalStorageAvailable()) {
        return await action(...args)
      }

      const data = getCachedResponse<R>(cacheKey)

      if (data) {
        execRequestWithEvent(...args)
        return data
      } else {
        return await execRequest(...args)
      }
    }
  }

  return wrappedAction
}
